import { useEffect, useMemo, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { loadStripe, type PaymentIntent, type StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { supabase } from '@/lib/customSupabaseClient';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { usePresaleAccess } from '@/hooks/usePresaleAccess';
import { PRESALE_VISIBLE, PRESALE_TERMS_URL } from '@/config/stripe';
import { SALES_AGREEMENT_VERSION } from '@/content/salesAgreement';

// ─── Stripe (browser side) ───────────────────────────────────────────────
// The publishable key decides the mode. pk_test = no real money moves, ever.
const PUBLISHABLE_KEY = (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined) ?? '';
const TEST_MODE = PUBLISHABLE_KEY.startsWith('pk_test');
const stripePromise = PUBLISHABLE_KEY ? loadStripe(PUBLISHABLE_KEY) : null;

// ─── Brand ───────────────────────────────────────────────────────────────
const C = {
  blue: 'var(--blue)',
  gold: 'var(--gold)',
  cream: 'var(--cream)',
  dark: 'var(--charcoal)',
  soft: 'color-mix(in srgb, var(--charcoal) 88%, transparent)',
  gray: 'color-mix(in srgb, var(--navy) 55%, transparent)',
  divider: 'color-mix(in srgb, var(--navy) 12%, transparent)',
};
const SANS = 'var(--sans)';
const SERIF = 'var(--serif)';

const money = (cents: number) =>
  (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });

// ─── Types ───────────────────────────────────────────────────────────────
type Tier = {
  slug: string;
  display_name: string;
  description: string | null;
  price_cents: number;
  credits_granted: number;
  brick_count: number;
  brick_types: string[];
  grants_membership: string | null;
  membership_duration_months: number | null;
  brick_cents: number | null;
  visits_cents: number | null;
  is_active: boolean;
};

type TaxRate = { code: string; label: string; rate_pct: number };

// What create-payment-intent charged, echoed back so the page shows Stripe's numbers.
type Breakdown = {
  price_cents: number;
  taxable_cents: number;
  tax_cents: number;
  tax_rate_pct: number | null;
  tax_label: string | null;
  total_cents: number;
};

type Availability = {
  can_sell: boolean;
  lays_hull: boolean;
  tier_cap: number | null;
  tier_committed: number;
  hull_cap: number;
  hull_committed: number;
  // Visits cap (migration 20260916_003): what this tier grants, the presale-wide cap, and
  // how many are already spoken for across every tier.
  grants_visits: number;
  visits_cap: number;
  visits_committed: number;
};

type Stage = 'loading' | 'blocked' | 'details' | 'pay' | 'result';

// Inscription rule (Sales Agreement v2 §6): three lines of about eighteen characters.
const INSCRIPTION_LINES = 3;
const INSCRIPTION_CHARS = 18;

function clampInscription(raw: string): string {
  return raw
    .split('\n')
    .slice(0, INSCRIPTION_LINES)
    .map((line) => line.slice(0, INSCRIPTION_CHARS))
    .join('\n');
}

// ─── Page ────────────────────────────────────────────────────────────────
export default function PresaleCheckoutPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { canCheckout, checking } = usePresaleAccess();

  const [tier, setTier] = useState<Tier | null>(null);
  const [avail, setAvail] = useState<Availability | null>(null);
  const [taxRate, setTaxRate] = useState<TaxRate | null>(null);
  const [stage, setStage] = useState<Stage>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);

  const [customerName, setCustomerName] = useState('');
  const [agreed, setAgreed] = useState(false); // §16 checkbox
  const [inscription, setInscription] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [breakdown, setBreakdown] = useState<Breakdown | null>(null);

  const [result, setResult] = useState<{ piId: string; piStatus: PaymentIntent.Status } | null>(null);

  // The buyer came back from a redirect-based payment method (bank login, etc.).
  // Stripe appends the PaymentIntent to the return URL; read its status and skip the form.
  const returnedSecret = searchParams.get('payment_intent_client_secret');

  // Load the tier and its availability.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [{ data: t, error: tErr }, { data: a, error: aErr }, { data: r }] = await Promise.all([
        supabase
          .from('tiers')
          .select(
            'slug, display_name, description, price_cents, credits_granted, brick_count, brick_types, grants_membership, membership_duration_months, brick_cents, visits_cents, is_active'
          )
          .eq('slug', slug)
          .maybeSingle(),
        supabase.rpc('tier_availability', { p_slug: slug }).maybeSingle(),
        // The rate the server applies to the brick part of the price (v2.tax_rates 'nyc'), shown so the
        // buyer sees the tax line before paying. The server's number is the one charged.
        supabase.from('tax_rates').select('code, label, rate_pct').eq('code', 'nyc').eq('is_active', true).maybeSingle(),
      ]);
      if (cancelled) return;
      if (tErr || !t) {
        setLoadError('That tier does not exist.');
        setStage('blocked');
        return;
      }
      if (aErr || !a) {
        setLoadError('Could not check availability. Try again in a moment.');
        setStage('blocked');
        return;
      }
      setTier(t as Tier);
      setAvail(a as Availability);
      if (r) setTaxRate({ code: r.code, label: r.label, rate_pct: Number(r.rate_pct) });
      setStage(returnedSecret ? 'result' : 'details');
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [slug, returnedSecret]);

  // Prefill the name from the profile.
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId) return;
    supabase
      .from('profiles')
      .select('first_name, last_name')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data }) => {
        const full = [data?.first_name, data?.last_name].filter(Boolean).join(' ').trim();
        if (full) setCustomerName((prev) => prev || full);
      });
  }, [userId]);

  // The payment form sits low on the page; when it gives way to the short "It's yours"
  // screen the viewport would be left scrolled into empty space. Jump back to the top.
  useEffect(() => {
    if (stage === 'result' || stage === 'pay') window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [stage]);

  // Resolve a redirect return into a result.
  useEffect(() => {
    if (!returnedSecret || !stripePromise) return;
    let cancelled = false;
    stripePromise.then(async (stripe) => {
      if (!stripe || cancelled) return;
      const { paymentIntent } = await stripe.retrievePaymentIntent(returnedSecret);
      if (paymentIntent && !cancelled) {
        setResult({ piId: paymentIntent.id, piStatus: paymentIntent.status });
        setStage('result');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [returnedSecret]);

  // Line items (ruled 9/16): the brick is the whole price and the visits come free with it, so the
  // visits show as "included" rather than a $0 line. Sales tax applies to the brick part only and
  // sits on top of the price as its own line. Once the server has created the PaymentIntent its
  // breakdown replaces the estimate, so the page and Stripe always agree.
  type Line = { label: string; amount: number | null; note?: string };
  const lines = useMemo(() => {
    if (!tier) return { items: [] as Line[], total: 0 };
    const items: Line[] = [];
    if (tier.brick_cents !== null && tier.brick_count > 0) {
      const brickLabel =
        tier.brick_count === 2
          ? 'Home Brick + Hull Brick'
          : tier.brick_types.includes('hull')
            ? 'Hull Brick'
            : 'Home Brick';
      items.push({ label: brickLabel, amount: tier.brick_cents });
      if (tier.credits_granted > 0) {
        items.push({
          label: `Includes ${tier.credits_granted} ${tier.credits_granted === 1 ? 'visit' : 'visits'}`,
          amount: tier.visits_cents && tier.visits_cents > 0 ? tier.visits_cents : null,
          note: 'included',
        });
      }
    } else {
      items.push({ label: tier.display_name, amount: tier.price_cents });
    }
    const taxable = breakdown?.taxable_cents ?? tier.brick_cents ?? 0;
    let tax = 0;
    if (taxable > 0) {
      if (breakdown) {
        tax = breakdown.tax_cents;
        items.push({ label: `${breakdown.tax_label ?? 'Sales tax'} (${breakdown.tax_rate_pct ?? 0}%)`, amount: tax });
      } else if (taxRate) {
        tax = Math.round((taxable * taxRate.rate_pct) / 100);
        items.push({ label: `${taxRate.label} (${taxRate.rate_pct}%)`, amount: tax });
      } else {
        items.push({ label: 'Sales tax', amount: null, note: 'added at payment' });
      }
    }
    return { items, total: breakdown?.total_cents ?? tier.price_cents + tax };
  }, [tier, taxRate, breakdown]);

  const hasBrick = (tier?.brick_count ?? 0) > 0;

  // Stable options object for <Elements>: Stripe warns if clientSecret changes between renders.
  const elementsOptions = useMemo<StripeElementsOptions | null>(
    () =>
      clientSecret
        ? {
            clientSecret,
            appearance: {
              theme: 'stripe',
              variables: { colorPrimary: '#286BCD', colorText: '#1C2B3A', fontFamily: 'Jost, sans-serif', borderRadius: '8px' },
            },
          }
        : null,
    [clientSecret]
  );
  const soldOut = avail ? !avail.can_sell : false;
  const launchBlocked = PRESALE_VISIBLE && !PRESALE_TERMS_URL;

  // ─── Create the hold + PaymentIntent, then show the payment form ────────
  const startPayment = async (e: FormEvent) => {
    e.preventDefault();
    if (!tier) return;
    if (!agreed) {
      setCreateError('Check the box to accept the Sales Agreement first.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error('Your session expired. Sign in again.');

      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        },
        body: JSON.stringify({
          tier_slug: tier.slug,
          purchase: true,
          brick_inscription: hasBrick && inscription.trim() ? clampInscription(inscription) : null,
          customer_name: customerName.trim() || null,
          // §16: recorded on the purchase row and in the PaymentIntent metadata.
          terms_accepted: agreed,
          terms_version: SALES_AGREEMENT_VERSION,
        }),
      });
      const data = (await res.json()) as { clientSecret?: string; breakdown?: Breakdown; error?: string; sold_out?: boolean };
      if (!res.ok || !data.clientSecret) {
        if (data.sold_out) {
          setAvail((prev) => (prev ? { ...prev, can_sell: false } : prev));
        }
        throw new Error(data.error ?? 'Could not start the payment.');
      }
      if (data.breakdown) setBreakdown(data.breakdown);
      setClientSecret(data.clientSecret);
      setStage('pay');
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Could not start the payment.');
    } finally {
      setCreating(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────
  const shell = (children: ReactNode) => (
    <>
      <Helmet>
        <title>Checkout — Maslow</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div style={{ backgroundColor: C.cream, color: C.dark, fontFamily: SERIF, minHeight: '100vh' }}>
        {TEST_MODE && (
          <div
            style={{
              backgroundColor: C.dark,
              color: C.cream,
              fontFamily: SANS,
              fontSize: 13,
              letterSpacing: 1,
              textAlign: 'center',
              padding: '10px 16px',
            }}
          >
            TEST MODE · no real charges · card 4242 4242 4242 4242, any future date, any CVC
          </div>
        )}
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '56px 24px 96px' }}>{children}</div>
      </div>
    </>
  );

  const label = (text: string) => (
    <p style={{ fontFamily: SANS, fontSize: 12, letterSpacing: 4, textTransform: 'uppercase', color: C.gray, marginBottom: 16 }}>
      {text}
    </p>
  );

  const fieldStyle: CSSProperties = {
    width: '100%',
    fontFamily: SANS,
    fontSize: 16,
    padding: '12px 14px',
    border: `1px solid ${C.divider}`,
    borderRadius: 8,
    backgroundColor: 'white',
    color: C.dark,
    outline: 'none',
  };

  const buttonStyle = (disabled: boolean): CSSProperties => ({
    fontFamily: SANS,
    fontSize: 15,
    fontWeight: 600,
    letterSpacing: 1,
    padding: '14px 28px',
    borderRadius: 8,
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    backgroundColor: disabled ? C.gray : C.blue,
    color: 'white',
    width: '100%',
  });

  if (checking || stage === 'loading') {
    return shell(<p style={{ fontFamily: SANS, color: C.gray }}>One moment.</p>);
  }

  if (!canCheckout) {
    return shell(
      <>
        {label('Not yet')}
        <h1 style={{ fontSize: 'clamp(26px, 5vw, 38px)', fontWeight: 400, marginBottom: 16 }}>This page isn't open.</h1>
        <p style={{ fontFamily: SANS, fontSize: 16, lineHeight: 1.7, color: C.soft, marginBottom: 24 }}>
          The presale opens to the list first.
        </p>
        <Link to="/build" style={{ fontFamily: SANS, color: C.blue }}>
          Back to the build
        </Link>
      </>
    );
  }

  if (launchBlocked) {
    return shell(
      <>
        {label('Checkout closed')}
        <h1 style={{ fontSize: 'clamp(26px, 5vw, 38px)', fontWeight: 400, marginBottom: 16 }}>Terms are not linked.</h1>
        <p style={{ fontFamily: SANS, fontSize: 16, lineHeight: 1.7, color: C.soft }}>
          PRESALE_VISIBLE is on but PRESALE_TERMS_URL is null (src/config/stripe.ts). Nothing can be sold without the
          agreement in front of the buyer. Set both in one commit.
        </p>
      </>
    );
  }

  if (stage === 'blocked' || !tier || !avail) {
    return shell(
      <>
        {label('Checkout')}
        <p style={{ fontFamily: SANS, fontSize: 16, color: C.soft, marginBottom: 24 }}>{loadError ?? 'Something went wrong.'}</p>
        <Link to="/build/preview" style={{ fontFamily: SANS, color: C.blue }}>
          Back to the tiers
        </Link>
      </>
    );
  }

  if (stage === 'result' && result) {
    return shell(<Outcome piId={result.piId} piStatus={result.piStatus} tier={tier} />);
  }

  // ─── Details + payment ──────────────────────────────────────────────────
  return shell(
    <>
      {label('Reserve your place')}
      <h1 style={{ fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: 400, marginBottom: 8 }}>{tier.display_name}</h1>
      {tier.description && (
        <p style={{ fontFamily: SANS, fontSize: 16, lineHeight: 1.7, color: C.soft, marginBottom: 28, maxWidth: 520 }}>
          {tier.description}
        </p>
      )}

      {/* What you get */}
      <div style={{ borderTop: `1px solid ${C.divider}`, borderBottom: `1px solid ${C.divider}`, padding: '20px 0', marginBottom: 28 }}>
        {tier.credits_granted > 0 && (
          <p style={{ fontFamily: SANS, fontSize: 15, lineHeight: 1.7, color: C.soft, margin: 0 }}>
            {tier.credits_granted} {tier.credits_granted === 1 ? 'visit' : 'visits'}, each a ten-minute private room plus one
            sample. Good from the day we open.
          </p>
        )}
        {tier.grants_membership && (
          <p style={{ fontFamily: SANS, fontSize: 15, lineHeight: 1.7, color: C.soft, margin: 0 }}>
            Founding membership: unlimited personal visits for a year from the day we open, twelve transferable guest
            passes, priority booking.
          </p>
        )}
        {hasBrick && (
          <p style={{ fontFamily: SANS, fontSize: 15, lineHeight: 1.7, color: C.soft, margin: 0 }}>
            {tier.brick_count === 2
              ? 'A Home Brick shipped to you during the build, and a Hull Brick laid in the floor.'
              : tier.brick_types.includes('hull')
                ? 'A Hull Brick, engraved and laid in the floor of the Hull.'
                : 'A Home Brick, cast terrazzo with the logo in brass, shipped to you during the build.'}
          </p>
        )}
      </div>

      {/* Line items */}
      <div style={{ marginBottom: 28 }}>
        {lines.items.map((l) => (
          <div key={l.label} style={{ display: 'flex', justifyContent: 'space-between', fontFamily: SANS, fontSize: 15, padding: '6px 0', color: C.soft }}>
            <span>{l.label}</span>
            <span style={l.amount === null ? { color: C.gray, fontSize: 12, letterSpacing: 2, textTransform: 'uppercase' } : undefined}>
              {l.amount === null ? l.note : money(l.amount)}
            </span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: SANS, fontSize: 17, fontWeight: 600, padding: '12px 0 0', borderTop: `1px solid ${C.divider}`, marginTop: 6 }}>
          <span>Total</span>
          <span>{money(lines.total)}</span>
        </div>
      </div>

      {soldOut ? (
        <div style={{ border: `1px solid ${C.divider}`, borderRadius: 8, padding: 20, fontFamily: SANS, fontSize: 15, color: C.soft }}>
          {!tier.is_active
            ? "This one isn't on the menu yet."
            : avail.lays_hull && avail.hull_committed >= avail.hull_cap
              ? 'The Hull bricks are spoken for.'
              : avail.grants_visits > 0 && avail.visits_committed + avail.grants_visits > avail.visits_cap
                ? 'The presale visits are spoken for.'
                : 'This tier is sold out.'}{' '}
          <Link to="/build/preview" style={{ color: C.blue }}>
            See what's left
          </Link>
        </div>
      ) : stage === 'details' ? (
        <form onSubmit={startPayment}>
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontFamily: SANS, fontSize: 13, color: C.gray, display: 'block', marginBottom: 6 }}>Name on the receipt</label>
            <input style={fieldStyle} value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Your name" autoComplete="name" />
          </div>

          {hasBrick && (
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontFamily: SANS, fontSize: 13, color: C.gray, display: 'block', marginBottom: 6 }}>
                Brick inscription, optional now. {INSCRIPTION_LINES} lines, {INSCRIPTION_CHARS} characters each. You have 60 days to add or change it.
              </label>
              <textarea
                style={{ ...fieldStyle, minHeight: 92, resize: 'vertical', fontFamily: 'ui-monospace, Menlo, monospace', letterSpacing: 1 }}
                value={inscription}
                onChange={(e) => setInscription(clampInscription(e.target.value))}
                placeholder={'Line one\nLine two\nLine three'}
                rows={INSCRIPTION_LINES}
              />
            </div>
          )}

          {createError && (
            <p style={{ fontFamily: SANS, fontSize: 14, color: '#B00020', marginBottom: 14 }}>{createError}</p>
          )}

          <AgreeBox checked={agreed} onChange={setAgreed} />

          <button
            type="submit"
            disabled={creating || !stripePromise || !agreed}
            style={buttonStyle(creating || !stripePromise || !agreed)}
          >
            {creating ? 'One moment' : 'Continue to payment'}
          </button>
          {!stripePromise && (
            <p style={{ fontFamily: SANS, fontSize: 13, color: C.gray, marginTop: 10 }}>
              VITE_STRIPE_PUBLISHABLE_KEY is not set in this build.
            </p>
          )}
        </form>
      ) : stage === 'pay' && clientSecret && stripePromise && elementsOptions ? (
        <Elements stripe={stripePromise} options={elementsOptions}>
          <PayForm
            amountCents={lines.total}
            bankFirst={tier.price_cents >= 15000}
            onDone={(piId, piStatus) => {
              setResult({ piId, piStatus });
              setStage('result');
            }}
          />
          <TermsLine />
        </Elements>
      ) : null}
    </>
  );
}

// ─── §16: the checkbox that accepts the Sales Agreement ──────────────────
// Label text is fixed by §16 of the agreement itself:
// "I have read and agree to the Maslow Sales Agreement and Terms of Service", agreement link in a new tab.
function AgreeBox({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  if (!PRESALE_TERMS_URL) {
    return (
      <p style={{ fontFamily: SANS, fontSize: 13, lineHeight: 1.6, color: C.gray, margin: '4px 0 16px' }}>
        Preview. The sales agreement is not linked yet; nothing on this page is public.
      </p>
    );
  }
  return (
    <label
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        fontFamily: SANS,
        fontSize: 14,
        lineHeight: 1.6,
        color: C.dark,
        margin: '4px 0 18px',
        cursor: 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 4, width: 18, height: 18, accentColor: C.blue, flexShrink: 0 }}
      />
      <span>
        I have read and agree to the{' '}
        <a href={PRESALE_TERMS_URL} target="_blank" rel="noreferrer" style={{ color: C.blue }}>
          Maslow Sales Agreement
        </a>{' '}
        and{' '}
        <a href="/terms-of-service" target="_blank" rel="noreferrer" style={{ color: C.blue }}>
          Terms of Service
        </a>
        .
      </span>
    </label>
  );
}

// ─── The agreement line under the Pay button ─────────────────────────────
function TermsLine() {
  return (
    <p style={{ fontFamily: SANS, fontSize: 13, lineHeight: 1.6, color: C.gray, marginTop: 16 }}>
      {PRESALE_TERMS_URL ? (
        <>
          You accepted the{' '}
          <a href={PRESALE_TERMS_URL} target="_blank" rel="noreferrer" style={{ color: C.blue }}>
            Maslow Sales Agreement
          </a>
          {' '}when you checked the box. Every pass reserves a future service. Not an investment, not a donation.
        </>
      ) : (
        <>Preview. The sales agreement is with counsel and is not linked yet; nothing on this page is public.</>
      )}
    </p>
  );
}

// ─── Payment form (must sit inside <Elements>) ───────────────────────────
function PayForm({
  amountCents,
  bankFirst,
  onDone,
}: {
  amountCents: number;
  bankFirst: boolean;
  onDone: (piId: string, piStatus: PaymentIntent.Status) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setErr(null);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}${window.location.pathname}` },
      redirect: 'if_required',
    });
    if (error) {
      setErr(error.message ?? 'The payment did not go through.');
      setBusy(false);
      return;
    }
    if (paymentIntent) onDone(paymentIntent.id, paymentIntent.status);
    setBusy(false);
  };

  return (
    <form onSubmit={submit}>
      <div style={{ marginBottom: 18 }}>
        <PaymentElement
          options={{
            layout: 'tabs',
            // The 9/4 rails ruling: bank transfer first on the $150+ tiers, card as the fallback.
            // Stripe only shows what is enabled in the dashboard; card-only until ACH is switched on.
            paymentMethodOrder: bankFirst ? ['us_bank_account', 'card'] : ['card', 'us_bank_account'],
          }}
        />
      </div>
      {err && <p style={{ fontFamily: SANS, fontSize: 14, color: '#B00020', marginBottom: 14 }}>{err}</p>}
      <button
        type="submit"
        disabled={!stripe || busy}
        style={{
          fontFamily: SANS,
          fontSize: 15,
          fontWeight: 600,
          letterSpacing: 1,
          padding: '14px 28px',
          borderRadius: 8,
          border: 'none',
          cursor: !stripe || busy ? 'not-allowed' : 'pointer',
          backgroundColor: !stripe || busy ? C.gray : C.blue,
          color: 'white',
          width: '100%',
        }}
      >
        {busy ? 'Working' : `Pay ${money(amountCents)}`}
      </button>
    </form>
  );
}

// ─── After payment: watch the purchase row until the webhook lands ───────
function Outcome({ piId, piStatus, tier }: { piId: string; piStatus: PaymentIntent.Status; tier: Tier }) {
  const [rowStatus, setRowStatus] = useState<string | null>(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    async function poll() {
      const { data } = await supabase.from('purchases').select('status').eq('stripe_payment_intent_id', piId).maybeSingle();
      if (cancelled) return;
      const s = data?.status ?? null;
      setRowStatus(s);
      const settled = s === 'active' || s === 'processing' || s === 'failed';
      if (!settled && tries < 15) {
        timer = window.setTimeout(() => setTries((t) => t + 1), 2000);
      }
    }
    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [piId, tries]);

  const paidByCard = piStatus === 'succeeded';
  const bankPending = piStatus === 'processing';

  let headline = 'Something is off.';
  let body = `Stripe reports "${piStatus}". Nothing was recorded. Email support@maslow.nyc with this reference: ${piId}.`;

  if (paidByCard && rowStatus === 'active') {
    headline = "It's yours.";
    body = `${tier.display_name} is on your account. A receipt is on its way from Stripe.`;
  } else if (paidByCard) {
    headline = 'Paid.';
    body = tries >= 15 ? `Payment went through; the record is taking longer than usual. Reference ${piId}.` : 'Recording it now.';
  } else if (bankPending) {
    headline = 'Your bank transfer is on its way.';
    body = 'It usually settles in about four business days. Your place is held in the meantime, and you will get a receipt when it clears.';
  }

  return (
    <>
      <p style={{ fontFamily: SANS, fontSize: 12, letterSpacing: 4, textTransform: 'uppercase', color: C.gray, marginBottom: 16 }}>
        {tier.display_name}
      </p>
      <h1 style={{ fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: 400, marginBottom: 16 }}>{headline}</h1>
      <p style={{ fontFamily: SANS, fontSize: 16, lineHeight: 1.7, color: C.soft, marginBottom: 28, maxWidth: 520 }}>{body}</p>
      <Link to="/profile" style={{ fontFamily: SANS, color: C.blue }}>
        Your profile
      </Link>
    </>
  );
}
